import React, { useState, useEffect } from "react";

import {
  useAppDispatch,
  useAppSelector,
  useHandlePageNavigate,
} from "../../../hooks/hooks";
import { useLocation } from "react-router-dom";

import { selectBrandInfo } from "../../../features/brand/brandSlice";
import {
  selectMemberData,
  selectIdProtectData,
} from "../../../features/authentication/authenticationSlice";
import {
  selectDownsellDetails,
  selectHasPdfUnlimited,
  selectIsInRebill,
  selectIsInTrial,
  selectPauseDetails,
  selectPrimarySubscription,
  unsetAccelerateBilling,
  updateIsInRebill,
} from "../../../features/subscriptions/subscriptionSlice";
import {
  setTab,
  toggleOpen,
} from "../../../features/multisearch/multisearchSlice";
import {
  capFirstLetter,
  formatPhoneNumber,
  scrollToHashElement,
} from "../../../utils";

import {
  Button,
  Padding,
  ReportTitle,
  Sheet,
  Text,
  Layout,
  CollapsableItemList,
  Container,
  AnchorLink,
  Icon,
  IconList,
  Tab,
  Modal,
  Margin,
} from "@kidslivesafe/sc-react";

import "./my-account.css";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";
import {
  selectNumberOfMonitoredReports,
  selectTotalAllowedMonitoredReports,
} from "../../../features/monitoring/monitoringSlice";
import UnpauseCTA from "../../../components/UIComponents/PauseComponents/UnpauseCTA";
import PauseBanner from "../../../components/UIComponents/PauseComponents/PauseBanner";
import { selectAccountMetrics } from "../../../features/reports/reportsSlice";
import ActivityHistoryContainer from "../../Dashboard/ActivityHistoryContainer";
import MonitoringContainerDashboard from "../../Dashboard/MonitoringContainer";
import ListDashboardContainer from "../../Dashboard/ListsContainer";
import NotesDashboardContainer from "../../Dashboard/NotesContainer";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";
import { usePauseSubscription } from "../../../hooks/pauseHook";
import PauseConfirmationModal from "../../../components/UIComponents/PauseConfirmationModal/PauseConfirmationModal";
import PdfUpsellModal from "../../../components/UIComponents/PdfUpsellModal/PdfUpsellModal";

const MyAccount: React.FC = () => {
  const navigate = useHandlePageNavigate();
  const dispatch = useAppDispatch();

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const query = useQuery();
  const cardUpdatedParam = query.get("cardUpdated");
  const isCardUpdated = cardUpdatedParam === "true";

  const brandInfo = useAppSelector(selectBrandInfo);
  const memberData = useAppSelector(selectMemberData);
  const pauseDetails = useAppSelector(selectPauseDetails);
  const subscriptionDetails = useAppSelector(selectPrimarySubscription);
  const currentBreakpoint = useCurrentBreakpoint();
  const idProtectData = useAppSelector(selectIdProtectData);
  const accountMetrics = useAppSelector(selectAccountMetrics);
  const downsellData = useAppSelector(selectDownsellDetails);
  const isInTrial = useAppSelector(selectIsInTrial);
  const isInRebill = useAppSelector(selectIsInRebill);
  const hasPdfUnlimited = useAppSelector(selectHasPdfUnlimited);

  const totalReportsMonitored = useAppSelector(selectNumberOfMonitoredReports);
  const totalAllowedReports = useAppSelector(
    selectTotalAllowedMonitoredReports,
  );

  const [showPausedModal, setShowPausedModal] = useState<boolean>(false);
  const [showPdfUpsellModal, setShowPdfUpsellModal] = useState<boolean>(false);
  const pauseSubscription = usePauseSubscription();

  const [idProtectModal, setIdProtectModal] = useState<boolean>(false);

  const handleSpotlightClick = (tab: Tab) => {
    dispatch(setTab(tab));
    dispatch(toggleOpen());
  };

  const handleIdProtectGetStarted = () => {
    if (idProtectData.isIdProtectAvailable || idProtectData.idProtectMember) {
      navigate("/id-protect");
      return;
    } else if (
      !idProtectData.isIdProtectAvailable ||
      !idProtectData.idProtectMember
    ) {
      setIdProtectModal(true);
      return;
    }
  };

  const handleExternalNavigation = async () => {
    // window.location.href = brandInfo.homeUrl + "/help-center/contact"; // This will open in the same tab
    window.open(
      `${brandInfo.homeUrl}/help-center/contact`,
      "_blank",
      "noopener",
    );
  };

  useEffect(() => {
    if (isCardUpdated) {
      dispatch(updateIsInRebill());
      dispatch(unsetAccelerateBilling());
    }
  }, [isCardUpdated]);

  //if there is an anchor hash, will scroll
  useEffect(() => {
    const scrollHeight = currentBreakpoint === "desktop" ? 75 : 80;

    // Scroll only if there's a hash in the URL
    const handleScroll = () => {
      // error occuriong where querySelector trying to be performed when it shouldnt inside of scrollToHashElement
      // ex. '#!/login'. '!' throws error.
      if (window.location.hash && !window.location.hash.includes("!")) {
        // Ensure a delay so that the page is fully rendered
        setTimeout(() => {
          scrollToHashElement(scrollHeight);
        }, 700);
      }
    };

    // Call on initial render or breakpoint change
    handleScroll();

    // Optional: Handle hash change events dynamically
    window.addEventListener("hashchange", handleScroll);

    return () => {
      // Cleanup the event listener
      window.removeEventListener("hashchange", handleScroll);
    };
  }, [currentBreakpoint]); // Re-run effect when breakpoint changes

  return (
    <>
      <ReportTitle reportTitle="My Account" />
      <Sheet>
        <Container altColor="transparent">
          <Padding bottom="md">
            <Layout
              type="flex"
              flexProps={{
                direction: "column",
                gap: "md",
              }}
            >
              <Container>
                <Padding
                  top={currentBreakpoint === "desktop" ? "md" : "lg"}
                  left="md"
                  right="md"
                  bottom="lg"
                >
                  <Layout
                    type="flex"
                    flexProps={{ direction: "column", gap: "lg" }}
                    breakpointStyles={{
                      tablet: {
                        type: "grid",
                        gridProps: {
                          templateColumns: "1-column",
                          gap: "md",
                        },
                      },
                      desktop: {
                        type: "grid",
                        gridProps: {
                          templateColumns: "2-column",
                          gap: "md",
                        },
                      },
                    }}
                  >
                    <Layout
                      type="flex"
                      flexProps={{ direction: "column", gap: "lg" }}
                    >
                      <Layout
                        type="flex"
                        flexProps={{ direction: "column", gap: "xs" }}
                      >
                        <Text tag="h2" fontWeight="bold">
                          Customer Information
                        </Text>
                        <Text fontWeight="bold">
                          First Name:{" "}
                          <Text fontWeight="normal" tag="span">
                            {memberData.firstname}
                          </Text>
                        </Text>
                        <Text fontWeight="bold">
                          Last Name:{" "}
                          <Text fontWeight="normal" tag="span">
                            {memberData.lastname}
                          </Text>
                        </Text>
                        <Text fontWeight="bold">
                          Email:{" "}
                          <Text fontWeight="normal" tag="span">
                            {memberData.username}
                          </Text>
                        </Text>
                        <Text fontWeight="bold">
                          Customer ID:{" "}
                          <Text fontWeight="normal" tag="span">
                            {memberData.customerId}
                          </Text>
                        </Text>
                      </Layout>
                    </Layout>

                    <Layout
                      type="flex"
                      flexProps={{ direction: "column", gap: "xs" }}
                    >
                      <Text tag="h2" fontWeight="bold">
                        Contact Support
                      </Text>
                      <Text fontWeight="bold">
                        Phone:{" "}
                        <Text fontWeight="normal" tag="span">
                          Call us toll-free at{" "}
                          <AnchorLink
                            inline
                            phoneNumber
                            hoverUnderline
                            href={brandInfo.phoneNumber}
                          >
                            {formatPhoneNumber(brandInfo.phoneNumber)}
                          </AnchorLink>
                          . Our automated system is available 24/7, 365-days a
                          year and live agent support available <br />
                          5:00 AM - 7:00 PM PT (Mon. - Fri.) and <br /> 7:00 AM
                          - 4:00 PM PT (Sat. & Sun.).
                        </Text>
                      </Text>
                      <Text fontWeight="bold">
                        Email:{" "}
                        <Text fontWeight="normal" tag="span">
                          Please use the{" "}
                          <AnchorLink
                            size="base"
                            href={`${brandInfo.homeUrl}/help-center/contact`}
                            inline
                            hoverUnderline
                          >
                            Contact Us
                          </AnchorLink>{" "}
                          page to send an email.
                        </Text>
                      </Text>
                    </Layout>

                    <Container
                      altColor={brandInfo.acronym === "pf" ? "alt2" : "alt"}
                      customClassName="page-my-account__usage-information"
                    >
                      <Padding space="md">
                        <Layout
                          type="flex"
                          flexProps={{ direction: "column", gap: "lg" }}
                        >
                          <Layout
                            type="flex"
                            flexProps={{ direction: "column", gap: "xs" }}
                          >
                            <Text tag="h2" fontWeight="bold">
                              Usage Information
                            </Text>
                            <Padding left="sm">
                              {/* <Text fontWeight="bold">
                        Monthly Reports:{" "}
                        <Text fontWeight="normal" tag="span">
                          {memberData.firstname}
                        </Text>
                      </Text>
                      <Text fontWeight="bold">
                        Top Search Type:{" "}
                        <Text fontWeight="normal" tag="span">
                          {memberData.lastname}
                        </Text>
                      </Text>
                          <Text fontWeight="bold">
                            Total Lifetime Searches:{" "}
                            <Text fontWeight="normal" tag="span">
                              {memberData.username}
                            </Text>
                          </Text> */}
                              <Text fontWeight="bold">
                                Current Reports Monitored:{" "}
                                <Text fontWeight="normal" tag="span">
                                  {
                                    accountMetrics.currentNumberOfReportsMonitored
                                  }
                                </Text>
                              </Text>
                              <Text fontWeight="bold">
                                Current List Count:{" "}
                                <Text fontWeight="normal" tag="span">
                                  {accountMetrics.currentNumberOfLists}
                                </Text>
                              </Text>
                              <Text fontWeight="bold">
                                Current Reports Added to Lists:{" "}
                                <Text fontWeight="normal" tag="span">
                                  {
                                    accountMetrics.CurrentNumberOfReportsAddedToLists
                                  }
                                </Text>
                              </Text>
                              <Text fontWeight="bold">
                                Current Reports with Notes:{" "}
                                <Text fontWeight="normal" tag="span">
                                  {accountMetrics.currentNumberOfNotes}
                                </Text>
                              </Text>
                            </Padding>
                          </Layout>
                        </Layout>
                      </Padding>
                    </Container>
                  </Layout>
                </Padding>
              </Container>
              <ReportTitle reportTitle="Report Access and History" />
              <ActivityHistoryContainer />
              <MonitoringContainerDashboard />
              <ListDashboardContainer />
              <NotesDashboardContainer />
            </Layout>
          </Padding>
        </Container>
      </Sheet>
      <Sheet>
        <Layout
          type="flex"
          flexProps={{
            direction: "column",
            gap: "md",
          }}
        >
          <Container
            customClassName="page-my-account__membership-information"
            containerId="membership-information"
          >
            <ReportTitle reportTitle="Account Management" />
            <Padding top="lg" left="md" right="md" bottom="lg">
              <Layout
                type="flex"
                flexProps={{ direction: "column", gap: "lg" }}
              >
                <Container>
                  <Layout
                    type="flex"
                    flexProps={{ direction: "column", gap: "lg" }}
                  >
                    <Text tag="h2">Account Overview</Text>

                    {/* pis in a ternary because labels changed */}
                    {["pis"].includes(brandInfo.acronym) ? (
                      <>
                        <Text fontWeight="bold">
                          Plan Status:{" "}
                          <Text
                            fontWeight="normal"
                            tag="span"
                            color={
                              subscriptionDetails?.cancelDate
                                ? "error"
                                : memberData.subscriptionStatus === "INACTIVE"
                                  ? "base"
                                  : "success"
                            }
                          >
                            {subscriptionDetails?.cancelDate
                              ? "Canceled"
                              : capFirstLetter(
                                  memberData.subscriptionStatus === "INACTIVE"
                                    ? memberData.subscriptionStatus
                                    : "ACTIVE",
                                )}{" "}
                          </Text>
                        </Text>
                        <Text fontWeight="bold">
                          Subscriptions Status:{" "}
                          <Text
                            fontWeight="normal"
                            tag="span"
                            color={
                              subscriptionDetails?.cancelDate
                                ? "error"
                                : memberData.subscriptionStatus === "INACTIVE"
                                  ? "base"
                                  : "success"
                            }
                          >
                            {memberData.subscriptionStatus === "PAUSED"
                              ? `Pause Pending until ${pauseDetails?.pauseAccessDate}`
                              : "Active"}{" "}
                            <UnpauseCTA type="link" />
                          </Text>
                        </Text>
                      </>
                    ) : (
                      <Layout type="flex" flexProps={{ gap: "xxs" }}>
                        <Text fontWeight="bold">
                          Status:{" "}
                          <Text
                            fontWeight="normal"
                            tag="span"
                            color={
                              subscriptionDetails?.cancelDate
                                ? "error"
                                : memberData.subscriptionStatus === "INACTIVE"
                                  ? "base"
                                  : "success"
                            }
                          >
                            {subscriptionDetails?.cancelDate
                              ? "Canceled"
                              : capFirstLetter(
                                  memberData!.subscriptionStatus
                                    ? memberData.subscriptionStatus!
                                    : "",
                                )}{" "}
                          </Text>
                        </Text>
                        <UnpauseCTA type="link" />
                      </Layout>
                    )}

                    <>
                      {!subscriptionDetails?.cancelDate ? (
                        <Layout
                          type="flex"
                          flexProps={{ direction: "column", gap: "xxxs" }}
                        >
                          <Text fontWeight="bold">Type of Account: </Text>

                          <Container>
                            {brandInfo.acronym === "pf" ? (
                              <Layout
                                type="flex"
                                flexProps={{
                                  direction: "column",
                                  gap: "xs",
                                }}
                              >
                                <Text fontWeight="normal" tag="span">
                                  {subscriptionDetails?.productName}
                                </Text>
                                {subscriptionDetails &&
                                subscriptionDetails!.propertyReportThreshold >
                                  0 ? (
                                  <Text fontWeight="normal" tag="span">
                                    {
                                      subscriptionDetails?.propertyReportThreshold
                                    }{" "}
                                    Property Reports/Month
                                  </Text>
                                ) : null}
                                {subscriptionDetails &&
                                subscriptionDetails!.peopleReportThreshold >
                                  0 ? (
                                  <Text fontWeight="normal" tag="span">
                                    {subscriptionDetails?.peopleReportThreshold}{" "}
                                    People Reports/Month
                                  </Text>
                                ) : null}
                                {hasPdfUnlimited ? (
                                  <Text fontWeight="normal" tag="span">
                                    PDF Downloads (Unlocked)
                                  </Text>
                                ) : (
                                  <Text fontWeight="normal" tag="span">
                                    <AnchorLink
                                      role="button"
                                      hoverUnderline
                                      inline
                                      handleClick={() => {
                                        setShowPdfUpsellModal(true);
                                      }}
                                    >
                                      Access PDF Downloads
                                    </AnchorLink>{" "}
                                  </Text>
                                )}

                                <Text fontWeight="normal" tag="span">
                                  Report Monitoring (
                                  <AnchorLink
                                    role="button"
                                    handleClick={() => {
                                      navigate(
                                        "/#dashboard__monitoring__container",
                                      );
                                    }}
                                    inline
                                    hoverUnderline
                                  >
                                    {totalReportsMonitored} used/
                                    {totalAllowedReports}
                                  </AnchorLink>
                                  )
                                </Text>
                              </Layout>
                            ) : (
                              <Layout
                                type="flex"
                                flexProps={{
                                  direction: "column",
                                  gap: "xs",
                                }}
                              >
                                <Text fontWeight="normal" tag="span">
                                  Multi-Search - Large
                                </Text>
                                <Text>
                                  <AnchorLink
                                    role="button"
                                    hoverUnderline
                                    inline
                                    handleClick={() => {
                                      handleSpotlightClick("people");
                                    }}
                                  >
                                    People Reports
                                  </AnchorLink>{" "}
                                  (Included)
                                </Text>
                                <Text>
                                  <AnchorLink
                                    role="button"
                                    hoverUnderline
                                    inline
                                    handleClick={() => {
                                      handleSpotlightClick("property");
                                    }}
                                  >
                                    Property Reports
                                  </AnchorLink>{" "}
                                  (Included)
                                </Text>
                                <Text>
                                  <AnchorLink
                                    role="button"
                                    hoverUnderline
                                    inline
                                    handleClick={() => {
                                      handleSpotlightClick("phone");
                                    }}
                                  >
                                    Phone Reports
                                  </AnchorLink>{" "}
                                  (Included)
                                </Text>
                                {["pdc"].includes(brandInfo.acronym) ? (
                                  <Text>
                                    <AnchorLink
                                      role="button"
                                      hoverUnderline
                                      inline
                                      handleClick={() => {
                                        handleSpotlightClick("vehicle");
                                      }}
                                    >
                                      Vehicle Reports
                                    </AnchorLink>{" "}
                                    (Included)
                                  </Text>
                                ) : null}
                              </Layout>
                            )}
                          </Container>
                        </Layout>
                      ) : null}
                      {!subscriptionDetails?.cancelDate &&
                      brandInfo.acronym !== "pf" ? (
                        <Layout
                          type="flex"
                          flexProps={{ direction: "column", gap: "xxxs" }}
                        >
                          <Text fontWeight="bold">Your Plan Details: </Text>

                          <Container>
                            <Layout
                              type="flex"
                              flexProps={{ direction: "column", gap: "xs" }}
                            >
                              <Text fontWeight="normal" tag="span">
                                Unlimited People Searches
                              </Text>
                              <Text fontWeight="normal" tag="span">
                                {subscriptionDetails?.peopleReportThreshold &&
                                subscriptionDetails.peopleReportThreshold >= 500
                                  ? "100"
                                  : subscriptionDetails?.peopleReportThreshold}{" "}
                                Reports / Month
                              </Text>
                              {hasPdfUnlimited ? (
                                <Text fontWeight="normal" tag="span">
                                  PDF Downloads (Unlocked)
                                </Text>
                              ) : (
                                <Text fontWeight="normal" tag="span">
                                  <AnchorLink
                                    role="button"
                                    hoverUnderline
                                    inline
                                    handleClick={() => {
                                      setShowPdfUpsellModal(true);
                                    }}
                                  >
                                    Access PDF Downloads
                                  </AnchorLink>{" "}
                                </Text>
                              )}
                              <Text fontWeight="normal" tag="span">
                                Report Monitoring (Included)
                              </Text>
                            </Layout>
                          </Container>
                        </Layout>
                      ) : null}
                    </>
                    <Layout
                      type="flex"
                      flexProps={{ direction: "column", gap: "xs" }}
                    >
                      <Text fontWeight="bold">Contact Support: </Text>
                      <AnchorLink
                        phoneNumber
                        hoverUnderline
                        href={brandInfo.phoneNumber}
                      >
                        {formatPhoneNumber(brandInfo.phoneNumber)}
                      </AnchorLink>
                    </Layout>
                    <Layout
                      type="flex"
                      flexProps={{ direction: "column", gap: "xs" }}
                    >
                      <Text fontWeight="bold">
                        Support Topics and Actions:{" "}
                      </Text>
                      <AnchorLink
                        size="base"
                        hoverUnderline
                        href={`${brandInfo.homeUrl}/help-center/faqs`}
                      >
                        Visit the Help Center
                      </AnchorLink>
                    </Layout>
                  </Layout>
                </Container>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "md",
                    alignItems: "flex-start",
                  }}
                  passThroughClassess="page-my-account__actions"
                >
                  <AnchorLink
                    size="base"
                    hoverUnderline
                    role="button"
                    disabled={subscriptionDetails?.cancelDate ? true : false}
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Button Click",
                          EventAction: "Update Billing Method",
                          MetaData: JSON.stringify({
                            key: "CARL",
                            value: "My Account",
                          }),
                        }),
                      );
                      navigate("/update-credit-card");
                    }}
                  >
                    I Would Like to Update My Billing Method
                  </AnchorLink>
                  <AnchorLink
                    size="base"
                    hoverUnderline
                    role="button"
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Button Click",
                          EventAction: "Reset Password",
                          MetaData: JSON.stringify({
                            key: "CARL",
                            value: "My Account",
                          }),
                        }),
                      );
                      navigate("/reset-password");
                    }}
                  >
                    I Would Like to Reset My Password
                  </AnchorLink>
                  <AnchorLink
                    size="base"
                    hoverUnderline
                    role="button"
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Button Click",
                          EventAction: "Increase Report Count",
                          MetaData: JSON.stringify({
                            key: "CARL",
                            value: "My Account",
                          }),
                        }),
                      );
                      handleExternalNavigation();
                    }}
                  >
                    I Would Like to Increase My Report Count
                  </AnchorLink>
                  <AnchorLink
                    size="base"
                    hoverUnderline
                    role="button"
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Button Click",
                          EventAction: "Decrease Report Count",
                          MetaData: JSON.stringify({
                            key: "CARL",
                            value: "My Account",
                          }),
                        }),
                      );
                      handleExternalNavigation();
                    }}
                  >
                    I Would Like to Decrease My Report Count
                  </AnchorLink>
                  {isInTrial || isInRebill ? null : (
                    <AnchorLink
                      size="base"
                      hoverUnderline
                      role="button"
                      disabled={
                        (memberData &&
                          memberData.subscriptionStatus === "PAUSED") ||
                        subscriptionDetails?.cancelDate
                          ? true
                          : false
                      }
                      handleClick={() => {
                        dispatch(
                          sendMemberEventAsync({
                            EventCategory: "Button Click",
                            EventAction: "Pause Account",
                            MetaData: JSON.stringify({
                              key: "CARL",
                              value: "My Account",
                            }),
                          }),
                        );
                        pauseSubscription();
                        setShowPausedModal(true);
                      }}
                    >
                      I Would Like to Pause My Membership
                    </AnchorLink>
                  )}
                  <AnchorLink
                    size="base"
                    hoverUnderline
                    role="button"
                    disabled={subscriptionDetails?.cancelDate ? true : false}
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Button Click",
                          EventAction: "Cancel Membership",
                          MetaData: JSON.stringify({
                            key: "CARL",
                            value: "My Account",
                          }),
                        }),
                      );
                      subscriptionDetails?.blueSnapMember
                        ? navigate("/manage-membership")
                        : navigate("/cancel-membership");
                      window.scrollTo(0, 0);
                    }}
                  >
                    I Would Like to Cancel My Membership
                  </AnchorLink>
                  <AnchorLink
                    size="base"
                    hoverUnderline
                    role="button"
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Button Click",
                          EventAction: "Contact Support",
                          MetaData: JSON.stringify({
                            key: "CARL",
                            value: "My Account",
                          }),
                        }),
                      );
                      handleExternalNavigation();
                    }}
                  >
                    I Would Like to Contact Support
                  </AnchorLink>
                </Layout>
              </Layout>
            </Padding>
          </Container>
          <Container customClassName="page-my-account__support-and-feedback">
            <ReportTitle reportTitle="Support and Feedback" />

            <Layout type="flex" flexProps={{ direction: "column", gap: "lg" }}>
              <CollapsableItemList>
                <Padding space="lg" left="md" right="md">
                  <Text>
                    Need help or have something to share? We’re here to support
                    you every step of the way.
                  </Text>
                </Padding>
                <Padding space="lg" left="md" right="md">
                  <Layout
                    type="flex"
                    flexProps={{ gap: "xs", direction: "column" }}
                  >
                    <Text fontWeight="bold">Phone Support</Text>
                    <Text>Call us toll-free at</Text>
                    <AnchorLink
                      phoneNumber
                      hoverUnderline
                      href={brandInfo.phoneNumber}
                      fontWeight="semibold"
                    >
                      {formatPhoneNumber(brandInfo.phoneNumber)}
                    </AnchorLink>

                    <ul className="page-my-account__support-and-feedback__list">
                      <li>
                        <Text>
                          Our automated system is available 24/7, 365 days a
                          year
                        </Text>
                      </li>
                      <li>
                        <Text>
                          Live agent support is available 5:00 AM - 7:00 PM PT
                          (Mon. - Fri.) and 7:00 AM - 4:00 PM PT (Sat. & Sun.)
                        </Text>
                      </li>
                    </ul>
                    <Padding top="xs">
                      <Text>
                        Whether you have a quick question or need more in-depth
                        assistance, we’re just a call away.
                      </Text>
                    </Padding>
                  </Layout>
                </Padding>
                <Padding space="lg" left="md" right="md">
                  <Layout
                    type="flex"
                    flexProps={{ gap: "xs", direction: "column" }}
                  >
                    <Text fontWeight="bold">Email Support</Text>
                    <Text>
                      Have a detailed question or want to share feedback?
                    </Text>
                    <Text>
                      Use our{" "}
                      <AnchorLink
                        size="base"
                        href={`${brandInfo.homeUrl}/help-center/contact`}
                        fontWeight="semibold"
                        inline
                        hoverUnderline
                      >
                        Contact Us
                      </AnchorLink>{" "}
                      page to send us an email and we’ll get back to you as
                      quickly as possible.
                    </Text>
                    <ul className="page-my-account__support-and-feedback__list">
                      <li>
                        <Text>
                          Our automated system is available 24/7, 365 days a
                          year
                        </Text>
                      </li>
                      <li>
                        <Text>
                          Live agent support is available 5:00 AM - 7:00 PM PT
                          (Mon. - Fri.) and 7:00 AM - 4:00 PM PT (Sat. & Sun.)
                        </Text>
                      </li>
                    </ul>
                    <Padding top="xs">
                      <Text>
                        If it's after hours, you prefer written communication,
                        or need to include detailed information, we're here to
                        help through email.
                      </Text>
                    </Padding>
                  </Layout>
                </Padding>
                <Padding space="lg" left="md" right="md">
                  <Layout
                    type="flex"
                    flexProps={{ gap: "xs", direction: "column" }}
                  >
                    <Text fontWeight="bold">Have an Idea or Request?</Text>
                    <Text>
                      We’re always looking to improve. Let us know what
                      features, data, or improvements you’d like to see.
                    </Text>
                    <ul className="page-my-account__support-and-feedback__list">
                      <li>
                        <AnchorLink
                          size="base"
                          href={`${brandInfo.homeUrl}/help-center/contact`}
                          fontWeight="semibold"
                          inline
                          hoverUnderline
                        >
                          Submit a Feature Request
                        </AnchorLink>
                      </li>
                      <li>
                        <AnchorLink
                          size="base"
                          href={`${brandInfo.homeUrl}/help-center/contact`}
                          fontWeight="semibold"
                          inline
                          hoverUnderline
                        >
                          Report a Bug
                        </AnchorLink>
                      </li>
                    </ul>
                  </Layout>
                </Padding>
                <Padding space="lg" left="md" right="md">
                  <Layout
                    type="flex"
                    flexProps={{ gap: "xs", direction: "column" }}
                  >
                    <Text fontWeight="bold">Helpful Resources</Text>
                    <Text>
                      Browse our support documentation and how-to guides:
                    </Text>
                    <AnchorLink
                      size="base"
                      href={`${brandInfo.homeUrl}/help-center/help-center`}
                      fontWeight="semibold"
                      hoverUnderline
                    >
                      Go to Help Center&#8594;
                    </AnchorLink>
                  </Layout>
                </Padding>
              </CollapsableItemList>
            </Layout>
          </Container>
        </Layout>
      </Sheet>
      <Sheet>
        {brandInfo.acronym === "pf" ? null : (
          <Padding bottom="sm">
            <Container
              altColor="transparent"
              customClassName="page-my-account__id-protect__container"
            >
              <Sheet>
                <Padding top="lg" left="md" right="md" bottom="lg">
                  <div className="page-my-account__id-protect__content">
                    <Layout
                      type="flex"
                      flexProps={{
                        gap: "lg",
                        direction: "column",
                        alignItems: "center",
                      }}
                    >
                      <div className="page-my-account__id-protect__icon">
                        <Icon
                          name={IconList.IconShield}
                          width={currentBreakpoint !== "desktop" ? 40 : 53}
                          height={currentBreakpoint !== "desktop" ? 47 : 60}
                        />
                      </div>
                      <Layout
                        type="flex"
                        flexProps={{ direction: "column", gap: "xxs" }}
                      >
                        <Text
                          fontWeight="bold"
                          size="xxxl"
                          textAlign="center"
                          passThroughClassess="page-my-account__id-protect__header"
                        >
                          ID Protect
                        </Text>
                        <Text
                          fontWeight="semibold"
                          tag="i"
                          size="xl"
                          color={`${brandInfo.acronym === "qpr" ? "base" : "white"}`}
                          textAlign="center"
                        >
                          Safeguard your identity and financial security with ID
                          Protect.
                        </Text>
                      </Layout>
                      <Text
                        fontWeight="normal"
                        textAlign="center"
                        color={`${brandInfo.acronym === "qpr" ? "base" : "white"}`}
                      >
                        Hackers and identity thieves continue to evolve. They
                        are very sophisticated, extremely creative and
                        relentless in their pursuit. Having the right solutions
                        to monitor and respond to identity theft is more
                        important than ever. Protect yourself with identity
                        monitoring and alerts, comprehensive restoration
                        services, $1 million identity theft insurance, and so
                        much more. Get the peace of mind you deserve for a
                        fraction of what the competition charges. Enroll in ID
                        Protect today!
                      </Text>
                      <Button
                        buttonText="Get Started"
                        handleClick={handleIdProtectGetStarted}
                        size="md"
                      />
                    </Layout>
                  </div>
                </Padding>
              </Sheet>
            </Container>
          </Padding>
        )}
      </Sheet>

      <Modal
        open={idProtectModal}
        setOpen={() => {
          setIdProtectModal(!idProtectModal);
        }}
        xClose
        outSideClose
      >
        <Margin top="xxl">
          <Padding top="lg">
            <Layout type="flex" flexProps={{ direction: "column", gap: "lg" }}>
              <Text fontWeight="bold" tag="h2" textAlign="center">
                Sorry, ID Protect is unavailable to purchase at this time
              </Text>
              <Text textAlign="center">
                Please contact member support at{" "}
                <AnchorLink role="link" phoneNumber inline>
                  {formatPhoneNumber(brandInfo.phoneNumber)}
                </AnchorLink>
              </Text>
            </Layout>
          </Padding>
        </Margin>
      </Modal>
      <PauseConfirmationModal
        showPausedModal={showPausedModal}
        setShowPausedModal={setShowPausedModal}
      />
      {/* Pause Banner - m35 */}
      <PauseBanner />
      {/* End of Pause Banner - m35 */}
      <PdfUpsellModal
        showPdfUpsellModal={showPdfUpsellModal}
        setShowPdfUpsellModal={setShowPdfUpsellModal}
        onSuccessfulPurchase={() => {}}
      />
    </>
  );
};

export default MyAccount;
